import React, { useState, useEffect, useRef } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation } from "swiper/modules";

import "swiper/css";
import "swiper/css/navigation";

import doorsData from "../data/doorsData.json";

const FeaturedDoors = () => {
  const [selectedDoor, setSelectedDoor] = useState(null);
  const [fullscreenImage, setFullscreenImage] = useState(null); // mobile fullscreen
  const [showFullDescription, setShowFullDescription] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [doorAnimated, setDoorAnimated] = useState(false);
  const [panOffset, setPanOffset] = useState({ x: 0, y: 0 });

  const imageWrapperRef = useRef(null);
  const isPanning = useRef(false);
  const lastPointer = useRef({ x: 0, y: 0 });
  const prevRef = useRef(null);
  const nextRef = useRef(null);

  useEffect(() => {
    if (selectedDoor) {
      setShowFullDescription(false);
      setZoomLevel(1);
      setPanOffset({ x: 0, y: 0 });
      setDoorAnimated(false);

      document.body.style.overflow = "hidden";
      document.body.classList.add("door-modal-open");

      const t = setTimeout(() => setDoorAnimated(true), 60);

      // Push history entry so back button closes modal instead of navigating away
      window.history.pushState({ modalOpen: true }, "");

      return () => {
        clearTimeout(t);
        document.body.style.overflow = "";
        document.body.classList.remove("door-modal-open");
      };
    }
  }, [selectedDoor]);

  // Intercept browser back button — close fullscreen first, then modal
  useEffect(() => {
    const handlePopState = (e) => {
      if (fullscreenImage) {
        setFullscreenImage(null);
        e.preventDefault();
      } else if (selectedDoor) {
        setSelectedDoor(null);
        e.preventDefault();
      }
    };
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [selectedDoor, fullscreenImage]);

  // Push history when fullscreen opens
  useEffect(() => {
    if (fullscreenImage) {
      window.history.pushState({ fullscreen: true }, "");
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = selectedDoor ? "hidden" : "";
    }
  }, [fullscreenImage, selectedDoor]);

  // Close on Escape key
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.keyCode === 27) setSelectedDoor(null);
    };
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, []);

  const featuredDoorCodes = [
    "SVR-601",
    "CS-29",
    "VL-404",
    "CS-06",
    "AD-45",
    "CG-712",
    "DS-02",
    "DS-03",
  ];

  const featuredDoors = doorsData.categories
    .flatMap((category) =>
      category.doors.map((door) => ({
        ...door,
        category: category.type,
        description: category.description,
      }))
    )
    .filter((door) => featuredDoorCodes.includes(door.code));

  const modalDescription =
    selectedDoor?.description ||
    "Premium designer door crafted with precision for modern interiors.";

  const handleZoomIn = (e) => {
    e?.stopPropagation?.();
    setZoomLevel((prev) => Math.min(prev + 0.25, 3));
  };

  const handleZoomOut = (e) => {
    e?.stopPropagation?.();
    setZoomLevel((prev) => Math.max(prev - 0.25, 1));
  };

  const handleZoomReset = (e) => {
    e?.stopPropagation?.();
    setZoomLevel(1);
    setPanOffset({ x: 0, y: 0 });
  };

  const handleMouseDown = (e) => {
    if (zoomLevel <= 1) return;
    e.preventDefault();
    isPanning.current = true;
    lastPointer.current = { x: e.clientX, y: e.clientY };
  };

  const handleMouseMove = (e) => {
    if (!isPanning.current) return;
    const dx = e.clientX - lastPointer.current.x;
    const dy = e.clientY - lastPointer.current.y;
    lastPointer.current = { x: e.clientX, y: e.clientY };
    setPanOffset((prev) => ({ x: prev.x + dx, y: prev.y + dy }));
  };

  const handleMouseUp = () => {
    isPanning.current = false;
  };

  const handleTouchStart = (e) => {
    if (zoomLevel <= 1) return;
    if (e.touches.length === 1) {
      isPanning.current = true;
      lastPointer.current = {
        x: e.touches[0].clientX,
        y: e.touches[0].clientY,
      };
    }
  };

  const handleTouchMove = (e) => {
    if (!isPanning.current || e.touches.length !== 1) return;
    e.preventDefault();
    const dx = e.touches[0].clientX - lastPointer.current.x;
    const dy = e.touches[0].clientY - lastPointer.current.y;
    lastPointer.current = {
      x: e.touches[0].clientX,
      y: e.touches[0].clientY,
    };
    setPanOffset((prev) => ({ x: prev.x + dx, y: prev.y + dy }));
  };

  const handleTouchEnd = () => {
    isPanning.current = false;
  };

  // Color palette matching Doors page
  const gold = "#D4AF37";

  return (
    <div>
      <style>{`
        .nav-wrapper {
          display: flex;
          align-items: center;
          gap: 14px;
          position: absolute;
          right: 0;
          top: 50%;
          transform: translateY(-50%);
          z-index: 20;
        }

        .custom-prev,
        .custom-next {
          cursor: pointer;
          font-size: 26px;
          font-weight: 600;
          color: #fc7303;
          transition: all 0.3s ease;
          user-select: none;
          padding: 4px 8px;
          line-height: 1;
        }

        .custom-prev:hover,
        .custom-next:hover {
          color: #888;
          transform: scale(1.15);
        }

        .swiper-button-next,
        .swiper-button-prev {
          display: none !important;
        }

        .featured-card {
          cursor: grab;
          transition: 0.3s;
          border: 1px solid #eee;
          background: #f8f6f2;
          padding: 15px;
          height: 100%;
        }

        .featured-card:active {
          cursor: grabbing;
        }

        /* ══════════════════════════════════════
           MODAL — matches Doors page exactly
        ══════════════════════════════════════ */
        .fd-modal-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0,0,0,0.85);
          backdrop-filter: blur(8px);
          -webkit-backdrop-filter: blur(8px);
          z-index: 3000;
          display: flex;
          justify-content: center;
          align-items: center;
          padding: 40px;
        }

        /* Close button: fixed, always on top */
        .fd-modal-close-btn {
          position: fixed;
          top: 20px;
          right: 20px;
          width: 45px;
          height: 45px;
          background: #fff;
          border: none;
          border-radius: 50%;
          cursor: pointer;
          z-index: 4000;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 4px 16px rgba(0,0,0,0.25);
          transition: background 0.2s ease, transform 0.2s ease;
        }
        .fd-modal-close-btn:hover {
          background: #f5f5f5;
          transform: scale(1.08);
        }

        .fd-modal-container {
          background: #fff;
          max-width: 1100px;
          width: 100%;
          display: flex;
          flex-direction: row;
          border-radius: 20px;
          overflow: hidden;
          box-shadow: 0 30px 60px rgba(0,0,0,0.5);
          position: relative;
          max-height: 88vh;
        }

        /* ── Image side ── */
        .fd-image-side {
          flex: 1.2;
          background: #F7F7F7;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 60px 60px 30px 60px;
          position: relative;
          -webkit-transform: translateZ(0);
          transform: translateZ(0);
        }

        /* Door open animation */
        .fd-door-panel {
          transform-origin: left center;
          transform: perspective(900px) rotateY(-75deg);
          opacity: 0;
          transition: transform 0.75s cubic-bezier(0.23, 1, 0.32, 1), opacity 0.4s ease;
          width: 100%;
          display: flex;
          justify-content: center;
        }
        .fd-door-panel.opened {
          transform: perspective(900px) rotateY(0deg);
          opacity: 1;
          animation: fd-flatten 0s 0.85s forwards;
        }
        @keyframes fd-flatten {
          to { transform: none; transform-style: flat; }
        }

        /* Pan/zoom wrapper */
        .fd-image-pan-wrapper {
          width: 100%;
          overflow: hidden;
          display: flex;
          align-items: center;
          justify-content: center;
          height: 60vh;
          border-radius: 8px;
          user-select: none;
          -webkit-user-select: none;
        }

        .fd-modal-image {
          width: 100%;
          max-height: 100%;
          object-fit: contain;
          filter: drop-shadow(10px 20px 40px rgba(0,0,0,0.15));
          display: block;
          image-rendering: -webkit-optimize-contrast;
          image-rendering: crisp-edges;
          -webkit-user-drag: none;
        }

        /* Zoom controls */
        .fd-zoom-controls {
          display: flex;
          align-items: center;
          gap: 6px;
          margin-top: 16px;
          background: rgba(255,255,255,0.9);
          border: 1px solid #e0d5c1;
          border-radius: 30px;
          padding: 5px 8px;
          box-shadow: 0 2px 10px rgba(0,0,0,0.08);
        }
        .fd-zoom-btn {
          background: none;
          border: none;
          cursor: pointer;
          color: #5D4037;
          display: flex;
          align-items: center;
          justify-content: center;
          width: 30px;
          height: 30px;
          border-radius: 50%;
          transition: background 0.2s ease;
          padding: 0;
        }
        .fd-zoom-btn:hover:not(:disabled) { background: rgba(212,175,55,0.15); }
        .fd-zoom-btn:disabled { opacity: 0.35; cursor: not-allowed; }
        .fd-zoom-reset-btn {
          background: none;
          border: 1px solid #e0d5c1;
          border-radius: 20px;
          padding: 3px 12px;
          cursor: pointer;
          font-size: 12px;
          font-weight: 700;
          font-family: 'Poppins', sans-serif;
          color: #5D4037;
          transition: all 0.2s ease;
          min-width: 48px;
          text-align: center;
        }
        .fd-zoom-reset-btn:hover { background: rgba(212,175,55,0.15); border-color: #D4AF37; }

        /* ── Details side: split into fixed header + scrollable body ── */
        .fd-details-side {
          flex: 1;
          display: flex;
          flex-direction: column;
          overflow: hidden;
        }

        /* Header: PREMIUM SERIES + Type + Model — pinned, never clips */
        .fd-details-header {
          padding: 50px 60px 25px 60px;
          flex-shrink: 0;
        }

        /* Body: description + sizes + button — scrolls when long */
        .fd-details-body {
          flex: 1;
          overflow-y: auto;
          padding: 0 60px 50px 60px;
          scrollbar-width: thin;
          scrollbar-color: #D4AF37 #f0ece4;
        }
        .fd-details-body::-webkit-scrollbar { width: 5px; }
        .fd-details-body::-webkit-scrollbar-track { background: #f0ece4; border-radius: 10px; }
        .fd-details-body::-webkit-scrollbar-thumb { background: #D4AF37; border-radius: 10px; }

        /* Description clamp */
        .fd-description {
          color: #666;
          line-height: 1.8;
          font-size: 1rem;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
          transition: all 0.4s ease;
          max-height: 120px;
        }
        .fd-description.expanded {
          -webkit-line-clamp: unset;
          max-height: 500px;
          overflow-y: auto;
          padding-right: 6px;
        }
        .fd-description.expanded::-webkit-scrollbar { width: 5px; }
        .fd-description.expanded::-webkit-scrollbar-thumb { background: #D4AF37; border-radius: 10px; }

        /* WhatsApp button */
        .fd-quote-btn {
          width: 100%;
          min-height: 56px;
          font-size: 1rem;
          cursor: pointer;
          background: #D4AF37;
          color: #fff;
          border: none;
          border-radius: 12px;
          font-weight: 700;
          text-decoration: none;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          padding: 16px 20px;
          transition: all 0.3s ease;
          box-shadow: 0 8px 20px rgba(212,175,55,0.25);
          font-family: 'Poppins', sans-serif;
        }
        .fd-quote-btn:hover {
          background: #c59f25;
          transform: translateY(-2px);
          box-shadow: 0 12px 25px rgba(212,175,55,0.35);
        }

        /* ══════════════════════════════════════
           RESPONSIVE
        ══════════════════════════════════════ */
        @media (max-width: 992px) {
          .fd-image-side, .fd-details-header { padding: 40px 40px 20px 40px; }
          .fd-details-body { padding: 0 40px 40px 40px; }
        }

        @media (max-width: 768px) {
          .nav-wrapper {
            gap: 10px;
            right: 0px;
            top: 70%;
            transform: translateY(-50%);
          }
          .custom-prev, .custom-next { font-size: 30px; padding: 2px 6px; }

          .fd-modal-overlay { padding: 0; align-items: flex-end; }
          .fd-modal-container {
            flex-direction: column;
            height: 92vh;
            max-height: 92vh;
            overflow-y: auto;
            border-radius: 20px 20px 0 0;
            width: 100%;
            max-width: 100%;
          }
          /* On mobile container scrolls — disable inner scroll */
          .fd-details-side { overflow: visible; }
          .fd-details-body { overflow-y: visible; flex: none; }
          .fd-details-header { padding: 20px 20px 15px 20px; }
          .fd-details-body { padding: 0 20px 20px 20px; }
          .fd-modal-close-btn { top: 12px; right: 12px; width: 38px; height: 38px; }
          .fd-image-side { padding: 30px 20px 10px 20px; }
          .fd-image-pan-wrapper { height: 38vh; }
        }

        @media (max-width: 576px) {
          .featured-title { font-size: 30px !important; }
          .featured-subtitle { font-size: 18px !important; letter-spacing: 2px !important; }
          .nav-wrapper {
            gap: 8px;
            right: -5px;
            top: 72%;
            transform: translateY(-50%);
          }
          .custom-prev, .custom-next { font-size: 26px; }
          .fd-image-pan-wrapper { height: 32vh; }
        }

        /* ══════════════════════════════════════
           TAP HINT — mobile only
        ══════════════════════════════════════ */
        .fd-tap-hint {
          display: none;
          text-align: center;
          font-size: 11px;
          color: #aaa;
          margin-top: 8px;
          font-family: 'Poppins', sans-serif;
        }
        @media (max-width: 768px) {
          .fd-tap-hint { display: block; }
        }

        /* ══════════════════════════════════════
           FULLSCREEN IMAGE OVERLAY
        ══════════════════════════════════════ */
        .fd-fullscreen-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0, 0, 0, 0.96);
          z-index: 9999;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 0;
          animation: fd-fs-in 0.22s ease;
        }
        @keyframes fd-fs-in {
          from { opacity: 0; transform: scale(0.96); }
          to   { opacity: 1; transform: scale(1); }
        }

        /* Close × — top right */
        .fd-fullscreen-close {
          position: fixed;
          top: 16px;
          right: 16px;
          width: 42px;
          height: 42px;
          border-radius: 50%;
          border: none;
          background: rgba(255,255,255,0.15);
          backdrop-filter: blur(6px);
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          z-index: 10001;
          transition: background 0.2s ease, transform 0.2s ease;
        }
        .fd-fullscreen-close:hover { background: rgba(255,255,255,0.25); transform: scale(1.08); }

        /* ← Back — top left */
        .fd-fullscreen-back {
          position: fixed;
          top: 16px;
          left: 16px;
          height: 42px;
          padding: 0 16px;
          border-radius: 30px;
          border: none;
          background: rgba(255,255,255,0.15);
          backdrop-filter: blur(6px);
          color: #fff;
          font-size: 14px;
          font-weight: 700;
          font-family: 'Poppins', sans-serif;
          display: flex;
          align-items: center;
          gap: 6px;
          cursor: pointer;
          z-index: 10001;
          transition: background 0.2s ease;
        }
        .fd-fullscreen-back:hover { background: rgba(255,255,255,0.25); }

        /* The fullscreen image */
        .fd-fullscreen-img {
          max-width: 100vw;
          max-height: 100vh;
          width: auto;
          height: auto;
          object-fit: contain;
          display: block;
          user-select: none;
          -webkit-user-drag: none;
          filter: drop-shadow(0 0 40px rgba(255,255,255,0.08));
        }
      `}</style>

      <section
        style={{
          padding: "60px 0",
          paddingTop: "30px",
          background: "#f8f6f2",
          overflow: "hidden",
        }}
      >
        <div className="container">
          <div
            style={{
              position: "relative",
              marginBottom: "30px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <div className="text-center">
              <h6
                className="featured-subtitle"
                style={{
                  color: "#fc7303",
                  letterSpacing: "3px",
                  fontWeight: "700",
                  margin: 0,
                  fontSize: "20px",
                }}
              >
                OUR COLLECTION
              </h6>
              <h2
                className="featured-title"
                style={{
                  fontFamily: "'Oswald', sans-serif",
                  fontWeight: "700",
                  margin: 0,
                  fontSize: "32px",
                }}
              >
                FEATURED DOORS
              </h2>
            </div>

            <div className="nav-wrapper">
              <div ref={prevRef} className="custom-prev">❮</div>
              <div ref={nextRef} className="custom-next">❯</div>
            </div>
          </div>

          <Swiper
            modules={[Navigation]}
            spaceBetween={15}
            slidesPerView={2.5}
            loop={true}
            grabCursor={true}
            allowTouchMove={true}
            navigation={{
              prevEl: prevRef.current,
              nextEl: nextRef.current,
            }}
            onBeforeInit={(swiper) => {
              swiper.params.navigation.prevEl = prevRef.current;
              swiper.params.navigation.nextEl = nextRef.current;
            }}
            breakpoints={{
              320: { slidesPerView: 2.5, spaceBetween: 10 },
              768: { slidesPerView: 3.5, spaceBetween: 20 },
              1024: { slidesPerView: 5, spaceBetween: 25 },
            }}
          >
            {featuredDoors.map((door, index) => (
              <SwiperSlide key={index}>
                <div
                  className="featured-card"
                  onClick={() => setSelectedDoor(door)}
                >
                  <img
                    src={door.image}
                    alt={door.code}
                    style={{
                      width: "100%",
                      height: "180px",
                      objectFit: "contain",
                      marginBottom: "10px",
                    }}
                  />
                  <h6
                    style={{
                      fontWeight: "700",
                      fontSize: "13px",
                      marginBottom: "5px",
                      color: "#333",
                    }}
                  >
                    {door.code}
                  </h6>
                  <p
                    style={{
                      fontSize: "11px",
                      color: "#777",
                      minHeight: "35px",
                      marginBottom: "10px",
                      lineHeight: "1.2",
                    }}
                  >
                    {door.description ? door.description.substring(0, 35) : "Premium designer door"}...
                  </p>
                  <span
                    style={{
                      color: "#fc7303",
                      fontWeight: "600",
                      fontSize: "11px",
                    }}
                  >
                    VIEW DETAILS →
                  </span>
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      </section>

      {/* ══════════════════════════════════════════
          MODAL — identical structure to Doors page
      ══════════════════════════════════════════ */}
      {selectedDoor && (
        <div
          className="fd-modal-overlay"
          onClick={() => setSelectedDoor(null)}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
        >
          {/* Close button: fixed top-right, always visible */}
          <button
            className="fd-modal-close-btn"
            onClick={() => setSelectedDoor(null)}
          >
            <svg width="18" height="18" viewBox="0 0 14 14" fill="none">
              <path d="M13 1L1 13M1 1L13 13" stroke="#333" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </button>

          <div
            className="fd-modal-container"
            onClick={(e) => e.stopPropagation()}
          >
            {/* ── Image Side ── */}
            <div className="fd-image-side">
              <div className={`fd-door-panel ${doorAnimated ? "opened" : ""}`}>
                <div
                  ref={imageWrapperRef}
                  className="fd-image-pan-wrapper"
                  style={{
                    cursor: zoomLevel > 1
                      ? isPanning.current ? "grabbing" : "grab"
                      : "default",
                  }}
                  onMouseDown={handleMouseDown}
                  onTouchStart={handleTouchStart}
                  onTouchMove={handleTouchMove}
                  onTouchEnd={handleTouchEnd}
                >
                  <img
                    src={selectedDoor.image}
                    alt={selectedDoor.code}
                    className="fd-modal-image"
                    draggable={false}
                    onClick={() => setFullscreenImage(selectedDoor.image)}
                    style={{
                      transform: `scale(${zoomLevel}) translate(${panOffset.x / zoomLevel}px, ${panOffset.y / zoomLevel}px)`,
                      transition: isPanning.current ? "none" : "transform 0.25s ease",
                      transformOrigin: "center center",
                    }}
                  />
                </div>
              </div>

              {/* Zoom Controls */}
              <div className="fd-zoom-controls" onClick={(e) => e.stopPropagation()}>
                <button className="fd-zoom-btn" onClick={handleZoomOut} title="Zoom Out" disabled={zoomLevel <= 0.5}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" width="16" height="16">
                    <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/><line x1="8" y1="11" x2="14" y2="11"/>
                  </svg>
                </button>
                <button className="fd-zoom-reset-btn" onClick={handleZoomReset} title="Reset Zoom">
                  <span>{Math.round(zoomLevel * 100)}%</span>
                </button>
                <button className="fd-zoom-btn" onClick={handleZoomIn} title="Zoom In" disabled={zoomLevel >= 3}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" width="16" height="16">
                    <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/><line x1="11" y1="8" x2="11" y2="14"/><line x1="8" y1="11" x2="14" y2="11"/>
                  </svg>
                </button>
              </div>
            </div>

            {/* Tap hint — mobile only */}
            <p className="fd-tap-hint">Tap image to view fullscreen</p>

            {/* ── Details Side ── */}
            <div className="fd-details-side">

              {/* Fixed header: never scrolls away */}
              <div className="fd-details-header">
                <span style={{ color: gold, fontWeight: "800", letterSpacing: "2px", fontSize: "0.85rem", textTransform: "uppercase" }}>
                  PREMIUM SERIES
                </span>
                <h2 style={{ margin: "10px 0", lineHeight: "1.3", fontSize: "2.5rem" }}>
                  <span style={{ color: "#1a1a1a", fontWeight: 600 }}>Type:</span>{" "}
                  <span style={{ color: gold, fontWeight: 700 }}>{selectedDoor.category}</span>
                  <br />
                  <span style={{ color: "#1a1a1a", fontWeight: 600 }}>Model:</span>{" "}
                  <span style={{ color: "#8B4513", fontWeight: 700 }}>{selectedDoor.code}</span>
                </h2>
                <div style={{ width: "60px", height: "5px", backgroundColor: gold, margin: "15px 0 0 0", borderRadius: "5px" }}></div>
              </div>

              {/* Scrollable body */}
              <div className="fd-details-body">
                <div style={{ marginBottom: "10px", marginTop: "10px" }}>
                  <p className={`fd-description ${showFullDescription ? "expanded" : ""}`}>
                    {modalDescription}
                  </p>
                  {modalDescription.length > 100 && (
                    <button
                      onClick={() => setShowFullDescription(!showFullDescription)}
                      style={{
                        margin: 0,
                        background: "none",
                        border: "none",
                        color: gold,
                        fontWeight: "700",
                        cursor: "pointer",
                        padding: 0,
                        fontSize: "0.95rem",
                        fontFamily: "'Poppins', sans-serif",
                      }}
                    >
                      {showFullDescription ? "View Less" : "View More"}
                    </button>
                  )}
                </div>

                <div style={{ background: "#f9f9f9", borderRadius: "15px", marginBottom: "10px", padding: "15px" }}>
                  <p style={{ color: "#999", margin: "0 0 5px 0", fontSize: "0.9rem" }}>Available Sizes</p>
                  <p style={{ color: "#1a1a1a", margin: 0, fontWeight: "700", fontSize: "1.1rem" }}>
                    {doorsData.doorSizes.join(", ")}
                  </p>
                </div>

                <a
                  href={`https://wa.me/918374771149?text=${encodeURIComponent(
                    `Hi, I would like to get a quote for this door.\n\nDoor Type: ${selectedDoor.category}\nDoor Model: ${selectedDoor.code}\n\nPlease share the price and available details.`
                  )}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="fd-quote-btn"
                >
                  <i className="fa-brands fa-whatsapp"></i>
                  Get Instant Quote
                </a>
              </div>

            </div>
          </div>
        </div>
      )}
    {/* ══════════════════════════════════════════
        FULLSCREEN IMAGE VIEWER (mobile)
    ══════════════════════════════════════════ */}
    {fullscreenImage && (
      <div
        className="fd-fullscreen-overlay"
        onClick={() => setFullscreenImage(null)}
      >
        {/* Close button */}
        <button
          className="fd-fullscreen-close"
          onClick={(e) => { e.stopPropagation(); setFullscreenImage(null); }}
        >
          <svg width="18" height="18" viewBox="0 0 14 14" fill="none">
            <path d="M13 1L1 13M1 1L13 13" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" />
          </svg>
        </button>

        {/* Back button */}
        <button
          className="fd-fullscreen-back"
          onClick={(e) => { e.stopPropagation(); setFullscreenImage(null); }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6"></polyline>
          </svg>
          Back
        </button>

        <img
          src={fullscreenImage}
          alt="Fullscreen door"
          className="fd-fullscreen-img"
          onClick={(e) => e.stopPropagation()}
          draggable={false}
        />
      </div>
    )}
    </div>
  );
};

export default FeaturedDoors;